/** Home screen preference keys (localStorage + account settings sync). */

export type HomeFriendsFilter = "online" | "all";

export const HOME_BOOL_SETTING_KEYS = [
  "settingNewsInHome",
  "settingNotificationsInHome",
  "settingHomeSplash",
  "settingHomeShowFriends",
  "settingHomeShowLastMode",
  "settingHomeCursorBounce",
  "settingHomeShowTrending",
  "settingHomeShowDots",
  "settingHomeShowStreak",
  "settingHomeShowChallenge",
  "settingHomeShowLootDrops",
  "settingHomeShowNewInShop",
  "settingHomeShowSuggested",
  "settingHomeShowContinue",
] as const;

export type HomeBoolSettingKey = (typeof HOME_BOOL_SETTING_KEYS)[number];

export const HOME_ALL_SETTING_KEYS = [
  ...HOME_BOOL_SETTING_KEYS,
  "settingHomeFriendsFilter",
  "settingHomeLayoutV1",
] as const;

const BOOL_DEFAULTS: Record<HomeBoolSettingKey, boolean> = {
  settingNewsInHome: false,
  settingNotificationsInHome: true,
  settingHomeSplash: true,
  settingHomeShowFriends: false,
  settingHomeShowLastMode: false,
  settingHomeCursorBounce: false,
  settingHomeShowTrending: false,
  settingHomeShowDots: false,
  settingHomeShowStreak: true,
  settingHomeShowChallenge: false,
  settingHomeShowLootDrops: false,
  settingHomeShowNewInShop: false,
  settingHomeShowSuggested: false,
  settingHomeShowContinue: false,
};

export function getBoolPref(key: string, def: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return def;
    return v === "1" || v === "true";
  } catch {
    return def;
  }
}

export function setBoolPref(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function homeBoolDefault(key: HomeBoolSettingKey): boolean {
  return BOOL_DEFAULTS[key] ?? false;
}

export function getHomeFriendsFilter(): HomeFriendsFilter {
  try {
    const v = localStorage.getItem("settingHomeFriendsFilter");
    return v === "all" ? "all" : "online";
  } catch {
    return "online";
  }
}

export function setHomeFriendsFilter(filter: HomeFriendsFilter): void {
  try {
    localStorage.setItem("settingHomeFriendsFilter", filter === "all" ? "all" : "online");
  } catch {
    /* ignore */
  }
}

/** Defaults for Settings reset / copy-paste share. */
export function defaultHomeSettingValue(key: string): string {
  if (key === "settingHomeFriendsFilter") return "online";
  if (key === "settingHomeLayoutV1") return "";
  if ((HOME_BOOL_SETTING_KEYS as readonly string[]).includes(key)) {
    return homeBoolDefault(key as HomeBoolSettingKey) ? "1" : "0";
  }
  return "";
}
